import { useState, useEffect, lazy, Suspense } from 'react';
import {
  APP_VERSION,
  BUILD_TIMESTAMP,
  GIT_SHA,
  VERSION_HISTORY,
} from '../../utils/version';
import { X, FileText, Tag, Clock, GitCommit, ExternalLink } from 'lucide-react';

// Lazy load ReactMarkdown for better initial bundle size
const ReactMarkdown = lazy(() => import('react-markdown'));

interface VersionDisplayProps {
  isAdmin?: boolean;
}

export function VersionDisplay({ isAdmin }: VersionDisplayProps) {
  const [showChangelog, setShowChangelog] = useState(false);

  const handleClick = () => {
    setShowChangelog(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className="flex flex-col items-center gap-1 text-xs text-gray-400 hover:text-gray-300 transition group cursor-pointer"
        title="Click to view changelog"
      >
        {/* Version with tag icon */}
        <div className="flex items-center gap-1.5">
          <Tag className="w-3 h-3" />
          <span className="font-mono">{APP_VERSION}</span>
        </div>

        {/* Timestamp and SHA on hover */}
        <div className="flex items-center gap-2 text-[10px] opacity-60 group-hover:opacity-100 transition-opacity">
          <div className="flex items-center gap-1">
            <Clock className="w-2.5 h-2.5" />
            <span className="font-mono">{BUILD_TIMESTAMP}</span>
          </div>
          <div className="flex items-center gap-1">
            <GitCommit className="w-2.5 h-2.5" />
            <span className="font-mono">{GIT_SHA}</span>
          </div>
        </div>
      </button>

      {showChangelog && (
        <ChangelogModal onClose={() => setShowChangelog(false)} />
      )}
    </>
  );
}

interface ChangelogModalProps {
  onClose: () => void;
}

function ChangelogModal({ onClose }: ChangelogModalProps) {
  const [changelogContent, setChangelogContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Fetch CHANGELOG.md from the project root (served via Vite)
    fetch('/CHANGELOG.md')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load changelog');
        return res.text();
      })
      .then((text) => {
        setChangelogContent(text);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Changelog load error:', err);
        setError('Unable to load changelog file.');
        setLoading(false);
      });
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Changelog</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                {APP_VERSION} / {BUILD_TIMESTAMP}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://github.com/your-org/pt-platform/releases"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 flex items-center gap-1"
            >
              <span>View Releases</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <button
              type="button"
              onClick={onClose}
              className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
              aria-label="Close changelog"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="space-y-4">
              <p className="text-amber-600 dark:text-amber-400 text-sm">{error} Showing inline history.</p>
              <ChangelogFallback />
            </div>
          ) : (
            <Suspense fallback={<div className="animate-pulse">Loading markdown renderer...</div>}>
              <article className="prose prose-sm dark:prose-invert max-w-none prose-headings:scroll-mt-4 prose-h2:text-lg prose-h2:font-semibold prose-h2:text-gray-800 dark:prose-h2:text-gray-200 prose-h3:text-base prose-h3:font-medium prose-ul:my-2 prose-li:my-0.5">
                <ReactMarkdown>{changelogContent}</ReactMarkdown>
              </article>
            </Suspense>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Fallback changelog display using VERSION_HISTORY from version.ts
 */
function ChangelogFallback() {
  return (
    <div className="space-y-6">
      {VERSION_HISTORY.slice(0, 20).map((entry, idx) => (
        <div key={idx} className="border-l-2 border-blue-500 pl-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-mono font-semibold text-gray-900 dark:text-white">
              {entry.version}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {entry.date}
            </span>
          </div>
          <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
            {entry.changes?.map((change, cIdx) => (
              <li key={cIdx} className="flex items-start gap-2">
                <span className="text-gray-400">•</span>
                <span>{change}</span>
              </li>
            )) || (
              <li className="text-gray-500 italic">
                {'notes' in entry ? (entry as { notes: string }).notes : 'No changes listed'}
              </li>
            )}
          </ul>
        </div>
      ))}
      <p className="text-xs text-gray-500 text-center pt-4">
        Showing most recent 20 versions. See version.ts for full history.
      </p>
    </div>
  );
}
